import { DashboardLayout } from "@/components/dashboard/layouts/DashboardLayout";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useInfiniteQuery } from "react-query";
import axios from "axios";
import { DropdownOption, PaginatedResponse } from "@/models/Dashboard";
import {
  Flow,
  FlowDetail,
  FlowRun,
  FlowRunDetail as FlowRunDetailModel,
  FlowStats,
} from "@/models/flows";
import { BASE_URL } from "@/dashboardQueries";
import {
  ChartArea,
  ChevronRightIcon,
  Info,
  ListChecks,
  RouteIcon,
  Vote,
  WorkflowIcon,
} from "lucide-react";
import { StatusBadge } from "@/components/dashboard/flows/StatusBadge";
import { useState, useEffect, useRef } from "react";
import { FlowRunDetail } from "@/components/dashboard/flows/flow-runs/FlowRunDetail";
import { Loading } from "@/components/portal/Loading";
import { useDarkMode } from "@/hooks";
import { flow, last } from "lodash";
import { formatDateWithTime, formatTimeFromDate } from "@/utils/date";
import { TabNav } from "@/components/dashboard/nav/TabNav";
import { ApprovalMode } from "@/components/dashboard/flows/ApprovalQueue";
import { Formik, FormikValues } from "formik";
import { Dropdown } from "@/crud/form/Dropdown";
import yup from "@/crud/yup-extended";
import { useInView } from "react-intersection-observer";
import { BackfillAlert } from "@/components/dashboard/flows/BackfillAlert";
import { Decisions } from "@/components/dashboard/flows/flow-runs/Decisions";
import { Input } from "@/crud/form/Input";
import { Button } from "@/components/ui/button";

const fetchFlowRuns = async (
  flowId: string,
  status?: DropdownOption,
  search?: string,
  pageParam: number = 1,
): Promise<PaginatedResponse<FlowRun>> => {
  let url = `${BASE_URL}/flow-runs?flow=${flowId}&page=${pageParam}`;

  if (status && status.id !== "all") {
    url += `&status=${status.id}`;
  }

  if (search) {
    url += `&displayName=${encodeURIComponent(search)}`;
  }

  const response = await axios.get(url);
  return response.data;
};

const fetchFlowById = async (flowId: string): Promise<FlowDetail> => {
  const response = await axios.get(`${BASE_URL}/flows/${flowId}`);
  return response.data;
};

const fetchFlowRunById = async (
  flowRunId: number,
): Promise<FlowRunDetailModel> => {
  const response = await axios.get(`${BASE_URL}/flow-runs/${flowRunId}`);
  return response.data;
};

const formatRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) return "N/A";
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

  if (diffInSeconds < 60)
    return diffInSeconds === 0 ? "just now" : `${diffInSeconds}s ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d ago`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths}mo ago`;
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears}y ago`;
};

const groupFlowRunsByDate = (flowRuns: PaginatedResponse<FlowRun>) => {
  return flowRuns.results.reduce(
    (groups: Record<string, FlowRun[]>, flowRun) => {
      const date = new Date(flowRun.startedAt).toLocaleDateString([], {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(flowRun);
      return groups;
    },
    {},
  );
};

export const FlowRuns = () => {
  const isDarkMode = useDarkMode();
  const { flowId } = useParams<{ flowId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const flowRunId = searchParams.get("flowRun");
  const [selectedFlowRunId, setSelectedFlowRunId] = useState<number | null>(
    flowRunId ? parseInt(flowRunId) : null,
  );
  const [selectedStatus, setSelectedStatus] = useState<DropdownOption>({
    id: "all",
    value: "All",
  });
  const [selectedTab, setSelectedTab] = useState("Runs");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Infinite query for flow runs
  const {
    data: flowRunsData,
    isLoading: isRunsLoading,
    isError: isRunsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["flowRuns", flowId, selectedStatus, search],
    ({ pageParam = 1 }) =>
      fetchFlowRuns(flowId, selectedStatus, search, pageParam),
    {
      getNextPageParam: (lastPage, allPages) => {
        // lastPage: { count, results, page, perPage }
        const totalPages = Math.ceil(lastPage.count / lastPage.perPage);
        if (lastPage.page < totalPages) {
          return lastPage.page + 1;
        }
        return undefined;
      },
      enabled: !!flowId,
      keepPreviousData: true,
    },
  );

  const {
    data: flowData,
    isLoading: isFlowLoading,
    isError: isFlowError,
  } = useQuery(["flow", flowId], () => fetchFlowById(flowId));

  const {
    data: selectedFlowRun,
    isLoading: isSelectedRunLoading,
    isError: isSelectedRunError,
  } = useQuery(
    ["flowRun", selectedFlowRunId],
    () => fetchFlowRunById(selectedFlowRunId!),
    {
      enabled: !!selectedFlowRunId, // Only fetch when a flow run is selected
    },
  );

  // Combine all results into a single array
  const allFlowRuns = flowRunsData?.pages.flatMap((page) => page.results) ?? [];

  // Group by date
  const groupedFlowRuns = groupFlowRunsByDate({
    results: allFlowRuns,
    count: allFlowRuns.length,
    next: null,
    previous: null,
  });

  // Intersection observer for infinite scroll
  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!flowId) {
    navigate("/dashboard/flows");
    return null;
  }

  // Update search params when selectedFlowRunId changes
  useEffect(() => {
    if (selectedFlowRunId) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("flowRun", selectedFlowRunId.toString());
        return params;
      });
    } else {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("flowRun");
        return params;
      });
    }
  }, [selectedFlowRunId, setSearchParams]);

  return (
    <DashboardLayout
      workInProgress="Flows are still a work in progress, and don’t reflect the
				final quality or functionality. Feedback welcome!"
      requiredPermissions={[]}
    >
      {flowData && (
        <div className="h-screen flex flex-col">
          <div className="p-4">
            <nav aria-label="Breadcrumb" className="flex">
              <ol role="list" className="flex items-center space-x-4">
                <li>
                  <div className="flex items-center">
                    <Link
                      to={`/dashboard/flows`}
                      className="text-sm font-medium dark:hover:text-white hover:text-gray-700 text-gray-500"
                    >
                      Flows
                    </Link>
                  </div>
                </li>
                <li>
                  <div className="flex items-center">
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-5 shrink-0 text-gray-500"
                    />
                    <span className="ml-4 text-sm font-medium dark:hover:text-white hover:text-gray-700 text-gray-500">
                      {flowData.flow.name}
                    </span>
                  </div>
                </li>

                <li>
                  <div className="flex items-center">
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-5 shrink-0 text-gray-500"
                    />
                    <span className="ml-4 text-sm font-medium dark:text-gray-300">
                      Flow Runs
                    </span>
                  </div>
                </li>
              </ol>
            </nav>
          </div>
          <div className="border-t dark:border-white/10 p-2">
            <TabNav
              items={[
                { name: "Runs", icon: WorkflowIcon },
                { name: "Approval Queue", icon: ListChecks },
                { name: "Decisions", icon: Vote },
              ]}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          </div>

          {selectedTab === "Decisions" && <Decisions flowId={flowId} />}

          {selectedTab === "Runs" && (
            <div className="overflow-x-scroll border-t border-gray-200 dark:border-white/10 h-full">
              {isRunsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loading colour={isDarkMode ? "white" : "black"} />
                </div>
              ) : (
                <>
                  {isRunsError && <p>Error loading flow runs.</p>}
                  {allFlowRuns.length > 0 ? (
                    <div className="flex h-full">
                      <aside className="xl:order-first xl:block xl:shrink-0 w-1/3 h-full">
                        <div className="relative flex flex-col h-full min-h-0 border-r border-gray-200 dark:border-white/10 h-full">
                          <div className="shrink-0 border-b dark:border-white/10">
                            <div className="flex h-12 flex-col justify-center bg-white dark:bg-zinc-900 px-4">
                              <div className="flex items-baseline space-x-3">
                                <h2 className="font-medium text-gray-900 dark:text-white truncate">
                                  {flowData.flow.name}
                                </h2>
                                <p className="text-sm font-medium text-gray-500 dark:text-zinc-500 text-nowrap">
                                  {flowRunsData?.pages[0]?.count} Flow Runs
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="border-b dark:border-white/10 p-3">
                            <Formik
                              initialValues={{ search: "" }}
                              enableReinitialize={false}
                              validationSchema={yup.object({})}
                              onSubmit={(values: FormikValues) => {
                                setSearch(values.search.trim());
                              }}
                            >
                              {({
                                values,
                                isValid,
                                submitForm,
                                initialValues,
                                dirty,
                                setFieldValue,
                                handleSubmit,
                              }) => {
                                return (
                                  <form onSubmit={handleSubmit}>
                                    <div className="flex">
                                      <div className="flex-1 pr-3">
                                        <Input
                                          name="search"
                                          type="text"
                                          placeholder="Search..."
                                          value={values.search}
                                        />
                                      </div>

                                      <div className="flex gap-2">
                                        <Button size="sm" type="submit">
                                          Search
                                        </Button>

                                        {search && (
                                          <Button
                                            size="sm"
                                            type="button"
                                            variant="outline"
                                            onClick={() => {
                                              setSearch("");

                                              setFieldValue("search", "");
                                            }}
                                          >
                                            Clear
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </form>
                                );
                              }}
                            </Formik>
                          </div>
                          <div>
                            <Formik
                              initialValues={{ status: selectedStatus }}
                              enableReinitialize={false}
                              validationSchema={yup.object({})}
                              onSubmit={(values: FormikValues) => {
                                setSelectedStatus(values.status);
                              }}
                            >
                              {({
                                values,
                                isValid,
                                submitForm,
                                initialValues,
                                dirty,
                                setFieldValue,
                              }) => {
                                // Track last submitted value in a ref
                                const lastSubmittedRef = useRef(initialValues);

                                useEffect(() => {
                                  if (isValid && dirty) {
                                    submitForm();
                                  }
                                  // eslint-disable-next-line
                                }, [values, isValid, dirty, submitForm]);

                                return (
                                  <form>
                                    <div className="flex p-3 border-b dark:border-white/10">
                                      <div className="w-1/2">
                                        <select
                                          className="block w-full rounded-md border-gray-300 dark:border-white/10 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                      dark:bg-zinc-900 dark:text-gray-300 text-gray-900"
                                          value={values.status.id}
                                          onChange={(e) => {
                                            const selectedOption = {
                                              id: e.target.value,
                                              value:
                                                e.target.options[
                                                  e.target.selectedIndex
                                                ].text,
                                            };
                                            setFieldValue(
                                              "status",
                                              selectedOption,
                                            );
                                          }}
                                        >
                                          <option value="all">All</option>
                                          <option value="running">
                                            Running
                                          </option>
                                          <option value="completed">
                                            Completed
                                          </option>
                                          <option value="failed">Failed</option>
                                          <option value="canceled">
                                            Canceled
                                          </option>
                                        </select>
                                      </div>
                                    </div>
                                  </form>
                                );
                              }}
                            </Formik>
                          </div>
                          <nav
                            aria-label="Message list"
                            className="flex-1 overflow-y-scroll min-h-0"
                          >
                            <ul role="list">
                              {Object.entries(groupedFlowRuns).map(
                                ([date, flowRuns]) => (
                                  <div key={date}>
                                    <li className="border-b dark:border-white/10 px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-zinc-800">
                                      {date}
                                    </li>
                                    {flowRuns.map(
                                      (flowRun: FlowRun, index: number) => (
                                        <li
                                          key={flowRun.id}
                                          onClick={() =>
                                            setSelectedFlowRunId(flowRun.id)
                                          }
                                          className={`border-b dark:border-white/10 relative px-6 py-5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-600 cursor-pointer ${
                                            selectedFlowRunId === flowRun.id
                                              ? "bg-zinc-100 dark:bg-zinc-700/40"
                                              : "bg-white dark:bg-zinc-900 hover:bg-gray-50 dark:hover:bg-zinc-800/40"
                                          }`}
                                        >
                                          <div className="flex justify-between space-x-3">
                                            <div className="min-w-0 flex-1">
                                              <a className="block focus:outline-none">
                                                <span
                                                  aria-hidden="true"
                                                  className="absolute inset-0"
                                                />
                                                {flowRun.displayName && (
                                                  <div className="dark:text-white text-zinc-600 mb-1">
                                                    {flowRun.displayName}
                                                  </div>
                                                )}
                                                {flowRun.status === "waiting" &&
                                                  flowRun.waitingOn && (
                                                    <div className="truncate text-sm text-gray-500 mb-2">
                                                      {
                                                        flowRun.waitingOn.step
                                                          .name
                                                      }
                                                    </div>
                                                  )}

                                                <StatusBadge
                                                  status={flowRun.status}
                                                />
                                              </a>
                                            </div>
                                          </div>
                                          <div className="mt-2">
                                            {flowRun.status === "completed" ? (
                                              <p className="line-clamp-2 text-sm text-gray-600">
                                                Finished{" "}
                                                {flowRun.finishedAt
                                                  ? formatDateWithTime(
                                                      flowRun.finishedAt,
                                                    )
                                                  : "N/A"}
                                              </p>
                                            ) : (
                                              <p className="line-clamp-2 text-sm text-gray-600">
                                                Started{" "}
                                                {flowRun.startedAt
                                                  ? formatRelativeTime(
                                                      flowRun.startedAt,
                                                    )
                                                  : "N/A"}
                                                )
                                              </p>
                                            )}
                                          </div>
                                        </li>
                                      ),
                                    )}
                                  </div>
                                ),
                              )}
                            </ul>
                            {/* Infinite scroll trigger */}
                            {hasNextPage && (
                              <div
                                ref={loadMoreRef}
                                className="py-4 flex justify-center"
                              >
                                {isFetchingNextPage ? (
                                  <span className="text-gray-400 text-sm">
                                    Loading more...
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">
                                    Scroll to load more...
                                  </span>
                                )}
                              </div>
                            )}
                          </nav>
                        </div>
                      </aside>
                      <main className="flex-1 p-4 flex flex-col h-full min-h-0">
                        {flowData.backfillableIds && (
                          <BackfillAlert flow={flowData} />
                        )}
                        {flowRunsData?.pages?.length > 0 && (
                          <>
                            {isSelectedRunLoading && (
                              <div className="flex items-center justify-center h-full -mt-96">
                                <Loading
                                  colour={isDarkMode ? "white" : "black"}
                                />
                              </div>
                            )}
                            {isSelectedRunError && (
                              <p>Error loading selected flow run.</p>
                            )}
                            {selectedFlowRun && (
                              <div>
                                <FlowRunDetail flowRun={selectedFlowRun} />
                              </div>
                            )}
                          </>
                        )}
                      </main>
                    </div>
                  ) : (
                    <p>
                      <div className="px-4 mt-4">
                        <div className="mt-3 h-40 border dark:border-white/10 border-dashed rounded-md px-4 flex justify-center items-center">
                          <div>
                            <div className="flex justify-center mb-4">
                              <RouteIcon className="h-10 w-10 mr-2 dark:text-gray-400 text-gray-900" />
                            </div>
                            <p className="dark:text-gray-400 text-gray-900 text-sm text-center">
                              There are no flow runs for this flow yet. Once
                              flow start executing, their runs will appear here.
                            </p>
                          </div>
                        </div>
                      </div>
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {selectedTab == "Approval Queue" && (
            <div className="border-t dark:border-white/10 flex-1 flex-col">
              <ApprovalMode flowId={flowData.flow.id} />
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
};
